/**
 * Metadata sidebar shown when multiple assets are selected.
 */

import React, { useState, useMemo } from "react";
import styled from "styled-components";
import {
  Box,
  Text,
  AssetType,
  ItemType,
  Flex,
  Button,
  useBoolean,
} from "@thenounproject/lingo-core";

import AccordionSection from "../../../app/_components/AccordionSection";
import InspectorMultiAssetTags from "./InspectorMultiAssetTags";
import InspectorMultiAssetColors from "./InspectorMultiAssetColors";
import InspectorMultiAssetDownload from "./use/InspectorMultiAssetDownload";
import ActionsMenu, { useActionMenuTrigger } from "@features/context-menus/ActionMenu";
import { Inspectable, isAssetInspectable } from "@constants/Inspector";
import InspectorActions from "./InspectorActions";
import InspectorCustomFields from "./InspectorCustomFields";

const Meta = styled(Box).attrs({
  width: "100%",
  height: "100%",
  borderTop: "1px solid border",
  overflow: "auto",
})`
  @media (min-width: 768px) {
    width: 260px;
    border-top: none;
  }
`;

const AssetName = styled(Text).attrs({
  font: "ui.regularBold",
  mb: "xxs",
})`
  overflow-wrap: break-word;
`;

export type Props = {
  inspectables: Inspectable[];
  canEdit: boolean;
};

function InspectorDetailedMultiple({ inspectables, canEdit }: Props) {
  const [colorsExpanded, setColorsExpanded] = useState(true),
    [actionsMenuOpen, setMenuOpen, setMenuClosed] = useBoolean(false);

  useActionMenuTrigger(() => {
    setMenuOpen();
    return true;
  });

  const hasNonAssetItems = useMemo(
      () => inspectables.some(i => i.item && i.item?.type !== ItemType.asset),
      [inspectables]
    ),
    hasFiles = useMemo(
      () => inspectables.some(i => isAssetInspectable(i) && i.asset.permalink),
      [inspectables]
    ),
    hasDownloadableAssets = useMemo(
      () =>
        inspectables.some(
          i => isAssetInspectable(i) && i.item?.displayProperties?.allowDownload !== false
        ),
      [inspectables]
    ),
    downloadableAssets = useMemo(
      () =>
        hasDownloadableAssets
          ? inspectables.filter(
              i => isAssetInspectable(i) && i.item?.displayProperties?.allowDownload !== false
            )
          : [],
      [inspectables, hasDownloadableAssets]
    ),
    hasColors = useMemo(
      () => inspectables.some(i => i.asset && i.asset.type === AssetType.color),
      [inspectables]
    ),
    showTags = useMemo(
      () => canEdit || inspectables.some(i => isAssetInspectable(i) && i.asset.keywords.length),
      [canEdit, inspectables]
    ),
    contentId = useMemo(() => {
      return inspectables
        .reduce((res, i) => {
          res.push(i.asset?.id ?? i.item?.id);
          return res;
        }, [])
        .join("-");
    }, [inspectables]),
    assetArray = useMemo(() => {
      return inspectables
        .filter(i => (i.item ? i.item.type === ItemType.asset : i.asset))
        .map(i => {
          return i.asset;
        });
    }, [inspectables]);

  const onlyColorAssets = hasColors && !hasFiles && !hasNonAssetItems,
    onlyNonAssetItems = hasNonAssetItems && !hasFiles && !hasColors;

  function renderFileActions() {
    if (onlyNonAssetItems) return null;
    const showMore = canEdit;
    return (
      <Flex mt="l" mb="s">
        {hasFiles && hasDownloadableAssets && (
          <InspectorMultiAssetDownload
            inspectables={downloadableAssets}
            styleOverrides={{ mr: "s", px: "none" }}
          />
        )}
        {showMore && (
          <Button
            data-popup-source="asset-actions"
            text="More"
            size="small"
            buttonStyle="outline"
            fontStyle="ui.small"
            icon="navigation.chevron-down"
            truncate={true}
            width={onlyColorAssets ? "100%" : 70}
            px="s"
            onClick={() => setMenuOpen()}
          />
        )}
        {actionsMenuOpen && (
          <ActionsMenu
            open={actionsMenuOpen}
            close={() => setMenuClosed()}
            source="asset-actions"
            inspectables={inspectables}
            vPos="floatBelow"
            hPos="alignRight"
          />
        )}
      </Flex>
    );
  }

  function renderHeaderSection() {
    return (
      <Box p="m" borderBottom="default">
        <Flex justifyContent="space-between" alignItems="center">
          <AssetName>{inspectables.length} selected</AssetName>
        </Flex>
        {renderFileActions()}
      </Box>
    );
  }

  function renderNonAssetActions() {
    if (!onlyNonAssetItems) return null;
    return (
      <AccordionSection>
        <InspectorActions inspectables={inspectables} canEdit={canEdit} />
      </AccordionSection>
    );
  }

  function renderColors() {
    if (!hasColors) return null;

    const colorsProps = { inspectables };
    return (
      <AccordionSection
        title="Copy colors"
        expanded={colorsExpanded}
        setExpanded={setColorsExpanded}
        contentId={contentId}>
        <InspectorMultiAssetColors {...colorsProps} />
      </AccordionSection>
    );
  }

  function renderSharedTags() {
    if (!showTags) return null;
    let assetInspectables: Inspectable[] = [];

    assetInspectables = inspectables.filter(i => isAssetInspectable(i));

    if (!assetInspectables.length) {
      return null;
    }

    const assetTagsProps = {
      inspectables: assetInspectables || inspectables,
      canEdit,
    };
    return <InspectorMultiAssetTags {...assetTagsProps} />;
  }

  function renderCustomFields() {
    if (hasNonAssetItems) return null;
    return (
      <AccordionSection>
        <InspectorCustomFields assets={assetArray} canEdit={canEdit} />
      </AccordionSection>
    );
  }

  return (
    <Meta data-testid="inspector-detailed-multiple">
      {renderHeaderSection()}
      {renderNonAssetActions()}
      {renderColors()}
      {renderSharedTags()}
      {renderCustomFields()}
    </Meta>
  );
}

export default InspectorDetailedMultiple;
